import { getAllSavedEmails, deleteSavedBatch, deleteSavedContact, retrySavedBatch, retrySavedContact, importToWorkspace, API_URL } from '../api/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import ImportSummaryModal from './ImportSummaryModal';
import axios from 'axios';
import {
    Copy, Loader2, Calendar,
    Users,
    Trash2, RefreshCw, Activity, ChevronDown, ChevronUp,
    DatabaseZap
} from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";

export default function SavedEmails() {
    const [savedContacts, setSavedContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [batches, setBatches] = useState([]);
    const [selectedBatchId, setSelectedBatchId] = useState(null);

    // Context / Env
    const API_BASE_URL = API_URL;

    // Action states
    const [deleteBatchOpen, setDeleteBatchOpen] = useState(false);
    const [deleteContactId, setDeleteContactId] = useState(null);
    const [operationLoading, setOperationLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});

    // Diagnostic states
    const [showSummary, setShowSummary] = useState(false);
    const [importResults, setImportResults] = useState(null);

    const groupIntoBatches = useCallback((contacts) => {
        const groups = {};
        const sorted = [...contacts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        sorted.forEach(contact => {
            const date = new Date(contact.createdAt);
            const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;
            if (!groups[key]) {
                groups[key] = {
                    id: key,
                    realBatchId: contact.batchId,
                    timestamp: date,
                    contacts: [],
                    stats: { valid: 0, invalid: 0, processing: 0 }
                };
            }
            groups[key].contacts.push(contact);
            if (contact.status === 'analyzing') groups[key].stats.processing++;
            else if (contact.emailStatus === 'Verified') groups[key].stats.valid++;
            else groups[key].stats.invalid++;
        });
        const batchList = Object.values(groups).sort((a, b) => b.timestamp - a.timestamp);
        setBatches(batchList);
        if (batchList.length > 0 && !selectedBatchId) {
            setSelectedBatchId(batchList[0].id);
        }
    }, [setBatches, setSelectedBatchId, selectedBatchId]);

    const fetchSavedContacts = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            // Source'u virgülle ayrılmış gönderiyoruz
            const { data } = await getAllSavedEmails({ source: 'email-finder,extension', limit: 2000 });
            if (data.data) {
                // Sadece veri değiştiyse güncelle (basit bir kontrol)
                setSavedContacts(data.data);
                groupIntoBatches(data.data);
            }
        } catch (err) {
            console.error('Kayitli veriler cekilemedi:', err);
            if (!silent) toast.error('Veriler yüklenirken hata oluştu');
        } finally {
            if (!silent) setLoading(false);
        }
    }, [setLoading, setSavedContacts, groupIntoBatches]);

    useEffect(() => {
        fetchSavedContacts();
    }, [fetchSavedContacts]);

    useEffect(() => {
        let interval;
        if (savedContacts.some(c => c.status === 'analyzing')) {
            interval = setInterval(() => {
                fetchSavedContacts(true); // silent refresh
            }, 3000);
        }
        return () => clearInterval(interval);
    }, [savedContacts, fetchSavedContacts]);

    const copyEmail = (email, e) => {
        if (e) e.stopPropagation();
        navigator.clipboard.writeText(email);
        toast.success('Email kopyalandı!');
    };

    const handleSyncToCrm = async (contacts) => {
        if (!contacts || contacts.length === 0) return;
        setOperationLoading(true);
        try {
            // First, find or default a workspaceId
            // In this foundation build, we fetch the first available workspace if none is explicitly chosen
            let workspaceIdToUse = localStorage.getItem('activeWorkspaceId');

            if (!workspaceIdToUse) {
                const token = localStorage.getItem('token');
                const wsRes = await axios.get(`${API_BASE_URL}/admin/mail-accounts`, { // Assuming we can fetch something that has workspace link or just fetch workspaces
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                // Mock fallback for now: try to get workspaceId from a mail account if available
                if (wsRes.data && wsRes.data[0]?.workspaceId) {
                    workspaceIdToUse = wsRes.data[0].workspaceId;
                } else {
                    // Critical fallback: Use a hardcoded or well-known ID for this stage of dev if necessary, 
                    // but better to ask or handle empty state.
                    // For now, let's try to get ALL workspaces if available.
                    try {
                        const allWsRes = await axios.get(`${API_BASE_URL}/user`, { // user info usually has workspaces
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (allWsRes.data.workspaces?.length > 0) {
                            workspaceIdToUse = allWsRes.data.workspaces[0]._id;
                        }
                    } catch (e) { }
                }
            }

            if (!workspaceIdToUse) {
                toast.error("Aktif bir workspace bulunamadı. Lütfen bir workspace seçin.");
                return;
            }

            const res = await importToWorkspace({
                workspaceId: workspaceIdToUse,
                contactIds: contacts.map(c => c._id)
            });

            setImportResults(res.data);
            setShowSummary(true);
            toast.success("Senkronizasyon tamamlandı!");

        } catch (err) {
            console.error('CRM sync error:', err);
            toast.error('Senkronizasyon başarısız: ' + (err.response?.data?.message || err.message));
        } finally {
            setOperationLoading(false);
        }
    };

    const handleDeleteBatch = async () => {
        if (!selectedBatch) return;
        const batchIdToDelete = selectedBatch.realBatchId || selectedBatch.contacts[0]?.batchId;

        setOperationLoading(true);
        try {
            if (batchIdToDelete) {
                // batchId varsa toplu sil
                await deleteSavedBatch(batchIdToDelete);
            } else {
                // batchId yoksa (eski kayıtlar / extension), tek tek sil
                for (const contact of selectedBatch.contacts) {
                    if (contact._id) {
                        await deleteSavedContact(contact._id);
                    }
                }
            }
            toast.success('İşlem silindi.');

            // Remove locally
            const contactIds = new Set(selectedBatch.contacts.map(c => c._id));
            const newContacts = savedContacts.filter(c =>
                batchIdToDelete ? c.batchId !== batchIdToDelete : !contactIds.has(c._id)
            );
            setSavedContacts(newContacts);
            groupIntoBatches(newContacts);
            setDeleteBatchOpen(false);
            if (batches.length > 1) setSelectedBatchId(batches[0].id === selectedBatch.id ? batches[1].id : batches[0].id);
            else setSelectedBatchId(null);

        } catch (err) {
            toast.error('Silme başarısız: ' + err.message);
        } finally {
            setOperationLoading(false);
        }
    };

    const handleDeleteContact = async () => {
        if (!deleteContactId) return;
        setOperationLoading(true);
        try {
            await deleteSavedContact(deleteContactId);
            toast.success('Kişi silindi.');

            // Update local
            const newContacts = savedContacts.filter(c => c._id !== deleteContactId);
            setSavedContacts(newContacts);
            groupIntoBatches(newContacts);
            setDeleteContactId(null);
        } catch (err) {
            toast.error('Silme başarısız');
        } finally {
            setOperationLoading(false);
        }
    };

    const handleRetryBatch = async () => {
        if (!selectedBatch) return;
        const batchIdToRetry = selectedBatch.realBatchId || selectedBatch.contacts[0]?.batchId;
        if (!batchIdToRetry) return toast.error("Batch ID bulunamadı");

        setOperationLoading(true);
        try {
            await retrySavedBatch(batchIdToRetry);
            toast.success('Yeniden deneme başlatıldı. Arka planda işleniyor...');
            // Reload after delay
            setTimeout(fetchSavedContacts, 1000);
        } catch (err) {
            toast.error('İşlem başlatılamadı');
        } finally {
            setOperationLoading(false);
        }
    };

    const handleRetryContact = async (id) => {
        setOperationLoading(true);
        try {
            await retrySavedContact(id);
            toast.success('Tekrar deneme başlatıldı.');
            setTimeout(fetchSavedContacts, 1000);
        } catch (err) {
            toast.error('Başlatılamadı');
        } finally {
            setOperationLoading(false);
        }
    };

    const deleteContact = (id, e) => {
        if (e) e.stopPropagation();
        setDeleteContactId(id);
    };

    const selectedBatch = batches.find(b => b.id === selectedBatchId);

    return (
        <div className="space-y-6 pt-12 animate-in fade-in duration-300">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Kaydedilen Taramalar</h1>
                    <p className="text-sm text-muted-foreground">Geçmişte yapılan tüm email bulma işlemleri ve sonuçları.</p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchSavedContacts} disabled={loading}>
                    <Loader2 className={cn("h-4 w-4 mr-2", loading ? "animate-spin" : "")} />
                    Yenile
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Sol Taraf: İşlem Listesi */}
                <Card className="md:col-span-1 h-[calc(100vh-200px)] flex flex-col">
                    <CardHeader className="pb-3 border-b">
                        <CardTitle className="text-base">Geçmiş İşlemler</CardTitle>
                        <CardDescription>{batches.length} işlem bulundu</CardDescription>
                    </CardHeader>
                    <div className="flex-1 overflow-auto p-2 space-y-2">
                        {loading && batches.length === 0 ? (
                            <div className="flex justify-center p-4"><Loader2 className="animate-spin" /></div>
                        ) : batches.length === 0 ? (
                            <div className="text-center p-4 text-sm text-muted-foreground">Henüz kayıt yok</div>
                        ) : (
                            batches.map(batch => (
                                <button
                                    key={batch.id}
                                    onClick={() => setSelectedBatchId(batch.id)}
                                    className={cn(
                                        "w-full text-left p-3 rounded-lg border transition-all text-sm hover:bg-muted/50",
                                        selectedBatchId === batch.id ? "bg-violet-50 border-violet-200 dark:bg-violet-900/20 dark:border-violet-800 ring-1 ring-violet-500/20" : "bg-card"
                                    )}
                                >
                                    <div className="flex justify-between items-start mb-1">
                                        <span className="font-medium flex items-center gap-1.5">
                                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                            {batch.timestamp.toLocaleDateString('tr-TR')}
                                        </span>
                                        <span className="text-[10px] text-muted-foreground">
                                            {batch.timestamp.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between mt-2">
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <Users className="h-3 w-3" /> {batch.contacts.length} Kişi
                                        </div>
                                        <div className="flex gap-1">
                                            {batch.stats.valid > 0 && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                    {batch.stats.valid}
                                                </span>
                                            )}
                                            {batch.stats.processing > 0 && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 animate-pulse">
                                                    <Loader2 className="h-2 w-2 mr-1 animate-spin" />
                                                    {batch.stats.processing}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </Card>

                {/* Sağ Taraf: Detay Tablosu */}
                <Card className="md:col-span-3 h-[calc(100vh-200px)] flex flex-col">
                    <CardHeader className="pb-3 border-b flex flex-row items-center justify-between">
                        <div>
                            <CardTitle className="text-base flex items-center gap-2">
                                {selectedBatch ? (
                                    <>
                                        <span className="text-muted-foreground font-normal">İşlem Detayı:</span>
                                        {selectedBatch.timestamp.toLocaleString('tr-TR')}
                                    </>
                                ) : 'İşlem Seçiniz'}
                            </CardTitle>
                        </div>
                        {selectedBatch && (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="default"
                                    size="sm"
                                    className="h-8 bg-violet-600 hover:bg-violet-700"
                                    onClick={() => handleSyncToCrm(selectedBatch.contacts)}
                                    disabled={operationLoading}
                                >
                                    <DatabaseZap className={cn("h-3.5 w-3.5 mr-1.5", operationLoading ? "animate-spin" : "")} />
                                    Sync to CRM
                                </Button>
                                <Button variant="outline" size="sm" className="h-8" onClick={handleRetryBatch} disabled={operationLoading}>
                                    <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", operationLoading ? "animate-spin" : "")} />
                                    Tümünü Tekrar Dene
                                </Button>
                                <Button variant="destructive" size="sm" className="h-8" onClick={() => setDeleteBatchOpen(true)} disabled={operationLoading}>
                                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                                    Tümünü Sil
                                </Button>
                            </div>
                        )}
                    </CardHeader>
                    <div className="flex-1 overflow-auto p-0">
                        {selectedBatch ? (
                            <table className="w-full caption-bottom text-sm text-left">
                                <thead className="[&_tr]:border-b sticky top-0 bg-background/95 backdrop-blur z-10">
                                    <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                                        <th className="h-10 px-4 align-middle font-medium text-muted-foreground">İsim</th>
                                        <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Unvan</th>
                                        <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Şirket</th>
                                        <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Email</th>
                                        <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Durum</th>
                                        <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-right">İşlem</th>
                                    </tr>
                                </thead>
                                <tbody className="[&_tr:last-child]:border-0">
                                    {selectedBatch.contacts.map((contact, i) => (
                                        <React.Fragment key={contact._id || i}>
                                            <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted group">
                                                <td className="p-3 align-middle font-medium">
                                                    {contact.firstName} {contact.lastName}
                                                </td>
                                                <td className="p-3 align-middle max-w-[200px] truncate" title={contact.title}>
                                                    {contact.title}
                                                </td>
                                                <td className="p-3 align-middle">
                                                    {contact.companyNameForEmails || contact.company}
                                                </td>
                                                <td className="p-3 align-middle font-mono text-xs">
                                                    {contact.foundEmails && contact.foundEmails.length > 0 ? (
                                                        <div className="flex flex-col gap-1">
                                                            {contact.foundEmails.map((fe, idx) => (
                                                                <div key={idx} className="flex items-center gap-1.5 group/email">
                                                                    <span className={cn(
                                                                        "inline-block w-1.5 h-1.5 rounded-full shrink-0",
                                                                        fe.status === 'Verified'
                                                                            ? 'bg-green-500'
                                                                            : fe.status === 'Likely Valid'
                                                                                ? 'bg-sky-500'
                                                                                : 'bg-yellow-500'
                                                                    )} />
                                                                    <span className="truncate max-w-[180px]" title={fe.email}>{fe.email}</span>
                                                                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 opacity-0 group-hover/email:opacity-100 transition-opacity shrink-0"
                                                                        onClick={(e) => copyEmail(fe.email, e)} title="Kopyala">
                                                                        <Copy className="h-2.5 w-2.5" />
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        contact.email || '—'
                                                    )}
                                                </td>
                                                <td className="p-3 align-middle">
                                                    {(() => {
                                                        if (contact.status === 'analyzing') {
                                                            return (
                                                                <div className="flex flex-col gap-1">
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 animate-pulse">
                                                                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                                                        {contact.currentAction || 'İşleniyor...'}
                                                                    </span>
                                                                    {contact.logs && contact.logs.length > 0 && (
                                                                        <span className="text-[10px] text-muted-foreground truncate max-w-[150px]" title={contact.logs[contact.logs.length - 1]}>
                                                                            {contact.logs[contact.logs.length - 1]}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                        const statusMap = {
                                                            'Verified': { label: 'Doğrulanmış', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
                                                            'Likely Valid': { label: 'Yüksek Güven', className: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
                                                            'Invalid': { label: 'Geçersiz', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
                                                            'Pending': { label: 'Bekliyor', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400' },
                                                            'Risky': { label: 'Riskli', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' }
                                                        };
                                                        const config = statusMap[contact.emailStatus] || statusMap['Invalid'];
                                                        return (
                                                            <div className="flex flex-col gap-1">
                                                                <span className={cn(
                                                                    "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide w-fit",
                                                                    config.className
                                                                )}>
                                                                    {config.label}
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="p-3 align-middle text-right">
                                                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-violet-600"
                                                            onClick={(e) => { e.stopPropagation(); handleSyncToCrm([contact]); }} title="Sync to CRM">
                                                            <DatabaseZap className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-primary"
                                                            onClick={(e) => copyEmail(contact.email, e)} title="Kopyala">
                                                            <Copy className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-blue-600"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setExpandedRows(prev => ({
                                                                    ...prev,
                                                                    [contact._id]: !prev[contact._id]
                                                                }));
                                                            }}
                                                            title="Detayları Göster">
                                                            {expandedRows[contact._id] ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-orange-600"
                                                            onClick={(e) => { e.stopPropagation(); handleRetryContact(contact._id); }} title="Tekrar Dene">
                                                            <RefreshCw className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600"
                                                            onClick={(e) => deleteContact(contact._id, e)} title="Sil">
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {expandedRows[contact._id] && (
                                                <tr className="bg-gray-50/50 dark:bg-gray-800/50 border-b">
                                                    <td colSpan="6" className="p-4 pl-12 text-sm text-muted-foreground">
                                                        <div className="space-y-2">
                                                            <h4 className="font-semibold text-xs uppercase tracking-wider text-gray-500 mb-1 flex items-center">
                                                                <Activity className="h-3 w-3 mr-1" />
                                                                İşlem Detayları & Loglar
                                                            </h4>
                                                            <div className="bg-background border rounded-md p-2 max-h-[150px] overflow-y-auto font-mono text-xs space-y-1 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
                                                                {contact.logs && contact.logs.length > 0 ? (
                                                                    contact.logs.map((log, idx) => (
                                                                        <div key={idx} className={cn(
                                                                            "flex items-start gap-2 py-0.5 border-b border-border/50 last:border-0",
                                                                            log.includes('✅') ? "text-green-600 dark:text-green-400" :
                                                                                log.includes('❌') ? "text-red-600 dark:text-red-400" :
                                                                                    "text-gray-600 dark:text-gray-300"
                                                                        )}>
                                                                            <span className="text-[10px] opacity-50 w-6 shrink-0 text-right">{idx + 1}.</span>
                                                                            <span>{log}</span>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <div className="text-gray-400 italic p-2 text-center">Henüz işlem kaydı yok.</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
                                <Users className="h-12 w-12 mb-4 opacity-20" />
                                <p>Sol taraftan bir işlem seçerek detayları görüntüleyin.</p>
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            {/* Delete Batch Dialog */}
            <AlertDialog open={deleteBatchOpen} onOpenChange={setDeleteBatchOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Tüm İşlemi Sil?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Bu işlemdeki {selectedBatch?.contacts.length} kişinin tüm verileri silinecek. Bu işlem geri alınamaz.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>İptal</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteBatch} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Evet, Sil
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Contact Dialog */}
            <AlertDialog open={!!deleteContactId} onOpenChange={(open) => !open && setDeleteContactId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Kişiyi Sil?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Bu kişi kaydı silinecek.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>İptal</AlertDialogCancel>
                        <AlertDialogAction onClick={() => {
                            if (deleteContactId) handleDeleteContact(deleteContactId);
                            setDeleteContactId(null);
                        }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Sil
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Import Summary Modal */}
            <ImportSummaryModal
                open={showSummary}
                onOpenChange={setShowSummary}
                results={importResults}
            />
        </div>
    );
}
